import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Hourglass, ArrowRight, Stamp, FileText } from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicDVC() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${BACKEND}/api/public/rapports/${token}`;
    fetch(url).then(async (r) => {
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Pièce introuvable");
      }
      return r.json();
    }).then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Lien copié dans le presse-papiers");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center label-mono">Chargement de la pièce vitaliste...</div>;

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Stamp size={48} weight="duotone" className="text-[var(--muted)]" />
        <h1 className="mt-4 font-serif text-3xl">Pièce introuvable</h1>
        <p className="mt-2 text-[var(--muted)]">{error}</p>
        <Link to="/" className="mt-8 border border-black px-5 py-2 label-mono hover:bg-black hover:text-[var(--paper)]">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      {/* Header */}
      <header className="border-b border-black sticky top-0 bg-[var(--paper)] z-10">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo">
            <Hourglass weight="fill" size={22} className="text-[var(--signal)]" />
            <div>
              <div className="font-serif text-xl font-semibold leading-none">VITA-LEX</div>
              <div className="label-mono text-[9px]">PIÈCE PUBLIQUE</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={copyLink} className="border border-black px-3 py-2 label-mono hover:bg-black hover:text-[var(--paper)]" data-testid="public-copy">
              Copier le lien
            </button>
            <Link to="/auth" className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2" data-testid="public-cta">
              Mesurer ma vie <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-4 sm:px-8 py-12">
        {/* Stamp banner */}
        <div className="border border-black bg-[var(--paper-2)] p-6 mb-10 flex flex-wrap items-center justify-between gap-4 fade-up">
          <div>
            <div className="label-mono">Pièce vitaliste publique · {data.type}</div>
            <div className="mt-2 font-serif text-2xl">
              {data.type === "RRC" ? "Requête de Reddition de Comptes" : "Déclaration Vitaliste Citoyenne"}
            </div>
            <div className="mt-2 font-mono text-xs text-[var(--muted)]">N° {data.id?.slice(0, 8)} · {fmtDate(data.cree_le)}</div>
          </div>
          {data.qualification && (
            <div className="px-5 py-3 stamp-rotate inline-block" style={{ background: data.qualification.couleur, color: "#fff" }}>
              <div className="label-mono text-[10px] opacity-90">Verdict</div>
              <div className="font-serif text-2xl">{data.qualification.niveau}</div>
            </div>
          )}
        </div>

        {/* Document */}
        <article className="border border-black bg-[var(--paper)] paper-grain fade-up fade-up-delay-1" data-testid="public-content">
          <div className="p-8 sm:p-12">
            <div className="text-center mb-10">
              <div className="inline-block stamp-rotate border-2 border-[var(--signal)] text-[var(--signal)] px-4 py-2 font-mono text-xs">
                {data.type === "RRC" ? "REDDITION DE COMPTES" : "DÉCLARATION CITOYENNE"}
              </div>
            </div>
            <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed text-[var(--ink)]">{data.contenu}</pre>
            <div className="mt-12 border-t border-black/15 pt-6 font-mono text-xs text-[var(--muted)] flex items-center justify-between">
              <div>VITA-LEX ANALYTICA · 2026</div>
              <div>{data.id?.slice(0, 8)}</div>
            </div>
          </div>
        </article>

        {/* CTA */}
        <div className="mt-16 border border-black bg-[var(--ink)] text-[var(--paper)] p-12 text-center fade-up fade-up-delay-2">
          <div className="label-mono opacity-70 text-[var(--paper)]">§ Et vous ?</div>
          <h2 className="mt-3 font-serif text-3xl sm:text-5xl font-medium leading-tight">
            Combien de jours de vie<br/>avez-vous sacrifiés ?
          </h2>
          <p className="mt-6 text-[var(--paper)]/70 max-w-xl mx-auto">
            Activez votre dossier vitaliste en moins de cinq minutes.
            Mesurez votre V1, votre jour de libération fiscale, votre Score de Retour Vitaliste.
          </p>
          <Link to="/auth" data-testid="public-cta-bottom"
            className="inline-flex mt-8 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono items-center gap-2">
            Ouvrir mon dossier <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </main>

      <footer className="border-t border-black/15 py-8 text-center label-mono">
        VITA-LEX ANALYTICA · 2026 · Démocratie budgétaire directe
      </footer>
    </div>
  );
}
